import { EventTimeline, EventType, MatrixClient, Room, RoomType as MatrixRoomType } from 'matrix-js-sdk/src/matrix';

import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

export const isMessengerCallRoom = (room: Room | null): boolean => {
  if (!room) return false;
  const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  const event = state?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL_STATE, '');
  return !!event?.getContent()?.messenger_room_id;
};

export const getMessengerRoomId = (room: Room | null): string | null => {
  if (!room) return null;
  const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  const event = state?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL_STATE, '');
  return (event?.getContent()?.messenger_room_id as string) ?? null;
};

export const findLinkedMessengerRoom = (matrixClient: MatrixClient, callSpaceId: string): Room | null =>
  matrixClient.getRooms().find((room) => {
    const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
    const event = state?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
    return event?.getContent()?.call_room_id === callSpaceId;
  }) ?? null;

import { RoomType as ChildRoomType } from '../components/BreakoutRooms/useRoomType';

export const getRoomStateEvents = (room: Room, eventType: string) =>
  room.getLiveTimeline().getState(EventTimeline.FORWARDS)?.getStateEvents(eventType) || [];

export const getCanonicalParentRoom = (room: Room) => {
  const matrixClient = room.client;
  if (!matrixClient) return null;

  const parentEvent = getRoomStateEvents(room, EventType.SpaceParent).find((event) => {
    const content = event.getContent<{ canonical?: boolean }>();
    return content?.canonical;
  });

  if (!parentEvent) return null;

  const parentRoomId = parentEvent.getStateKey();
  if (!parentRoomId) return null;

  return matrixClient.getRoom(parentRoomId) ?? null;
};

/** Walks canonical parents until a workspace the user has joined (in `joinedWorkspaces`). */
export const resolveJoinedWorkspaceIdForRoom = (
  matrixClient: MatrixClient,
  roomId: string,
  joinedWorkspaces: Record<string, unknown>
): string | null => {
  let room = matrixClient.getRoom(roomId);
  if (!room) return null;

  while (room) {
    const parent = getCanonicalParentRoom(room);
    if (!parent) return null;

    const parentId = parent.roomId;
    if (parentId in joinedWorkspaces) return parentId;

    room = parent;
  }

  return null;
};

/**
 * Workspace to select when opening any room (messenger chat OR call/meeting space).
 * - workspace id string — room resolves to that joined workspace via its canonical chain
 * - null — personal messenger chat (no canonical parent)
 * - undefined — room unknown, or target cannot be determined (e.g. orphan call space)
 *
 * Room-kind aware: call/meeting spaces never use the "no parent = Personal" branch.
 * They delegate to their linked messenger chat (`MESSENGER_CALL_STATE.messenger_room_id`)
 * so a legacy instant-call space without a `SpaceParent` resolves to the chat's workspace
 * instead of being mis-read as a Personal DM.
 */
export const resolveWorkspaceTargetForRoom = (
  matrixClient: MatrixClient,
  roomId: string,
  joinedWorkspaces: Record<string, unknown>
): string | null | undefined => {
  const room = matrixClient.getRoom(roomId);
  if (!room) return undefined;

  const joinedWorkspaceId = resolveJoinedWorkspaceIdForRoom(matrixClient, roomId, joinedWorkspaces);
  if (joinedWorkspaceId) return joinedWorkspaceId;

  if (isMessengerCallRoom(room)) {
    const messengerRoomId = getMessengerRoomId(room);
    if (!messengerRoomId) return undefined;
    return resolveWorkspaceTargetForRoom(matrixClient, messengerRoomId, joinedWorkspaces);
  }

  if (!getCanonicalParentRoom(room)) return null;

  return undefined;
};

/**
 * Workspace to select when opening a messenger room.
 * - workspace id string — room belongs to that joined workspace
 * - null — personal messenger chat (no canonical parent)
 * - undefined — room unknown or target cannot be determined
 */
export const resolveMessengerRoomWorkspaceTarget = (
  matrixClient: MatrixClient,
  roomId: string,
  joinedWorkspaces: Record<string, unknown>
): string | null | undefined => {
  const room = matrixClient.getRoom(roomId);
  if (!room) return undefined;

  const joinedWorkspaceId = resolveJoinedWorkspaceIdForRoom(matrixClient, roomId, joinedWorkspaces);
  if (joinedWorkspaceId) return joinedWorkspaceId;

  if (!getCanonicalParentRoom(room)) return null;

  return undefined;
};

/** Switches active workspace when opening a messenger room from a notification click. */
export const switchWorkspaceForMessengerRoom = (
  matrixClient: MatrixClient,
  roomId: string,
  joinedWorkspaces: Record<string, unknown>,
  currentWorkspaceId: string | null,
  setWorkspaceId: (id: string | null) => void
): void => {
  const target = resolveWorkspaceTargetForRoom(matrixClient, roomId, joinedWorkspaces);
  if (target !== undefined && target !== currentWorkspaceId) setWorkspaceId(target);
};

export const hasWorkspaceMeta = (room: Room) =>
  getRoomStateEvents(room, CustomMeedioMatrixEvents.SPACE_META).length > 0;

export const hasChildOfType = (room: Room, type: ChildRoomType) =>
  getRoomStateEvents(room, EventType.SpaceChild).some((event) => event.getContent()?.roomType === type);

const hasChatChild = (room: Room) => hasChildOfType(room, ChildRoomType.CHAT);

export const isMeetingRoom = (room: Room) => {
  const isSpaceRoom = room.getType() === MatrixRoomType.Space;
  const isWorkspace = hasWorkspaceMeta(room);
  const hasChatRoom = hasChatChild(room);

  return isSpaceRoom && !isWorkspace && hasChatRoom;
};

export const isWorkspaceRoom = (room: Room) => room.getType() === MatrixRoomType.Space && hasWorkspaceMeta(room);

export const isMainRoom = (room: Room): boolean => {
  if (!isMeetingRoom(room)) return false;

  const parentRoom = getCanonicalParentRoom(room);
  const isParentWorkspace = parentRoom ? hasWorkspaceMeta(parentRoom) : false;

  return !parentRoom || isParentWorkspace;
};

export const isBreakoutRoom = (room: Room): boolean => {
  if (!isMeetingRoom(room)) return false;

  const parentRoom = getCanonicalParentRoom(room);
  if (!parentRoom) return false;

  return !hasWorkspaceMeta(parentRoom);
};
